"use client";

/**
 * Phase 9 (origin) / Phase 11 Plan 11-05 (retargeted) — Smart-route shared CTA
 * for the £249 "We Set It Up For You" service.
 *
 * Phase 9 routed the logged-out branch to `/signup?redirect_url=...` and the
 * free branch through a CheckoutSummary modal with a Pro+addon bundle. Phase
 * 11 supersedes both: logged-out + free both fire the
 * createSetupServiceCheckoutSession server action which creates a £249-only
 * Stripe Checkout session and redirects the browser straight to
 * checkout.stripe.com.
 *
 * Auth-state branches:
 *
 *   logged_out                          → server action submit
 *                                          (Stripe Checkout, no signup form)
 *   free                                → server action submit
 *                                          (Stripe Checkout, no Pro bundle)
 *   pro                                 → /settings?tab=billing#setup-service
 *                                          (Phase 9 D-06 — preserved)
 *   pro_with_setup_purchased            → disabled (parent renders the chip)
 *   pro_with_setup_intake_submitted     → disabled (parent renders the chip)
 *
 * Flag gate: the CTA renders nothing unless the homegrown DB-backed
 * `setup_service` flag is true. Even with the flag on, the server action
 * applies its own defence-in-depth flag check (so a stale UI page can't
 * bypass a flag flip-off).
 *
 * The rendered <button> carries `data-testid="setup-service-cta"` for the
 * Phase 11 Playwright spec and any future E2E suite to locate it
 * deterministically.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useAppFeatureFlag } from "@/components/feature-flags/feature-flags-provider";
import { createSetupServiceCheckoutSession } from "@/app/api/setup-service/checkout/server-action";
import {
  readBrowserAttributionWithCurrentUrl,
  readAdvertisingConsent,
  readAnalyticsConsent,
  readCookieValue,
} from "@/components/analytics/browser-attribution";
import { subscribeToBrowserConsent } from "@/components/analytics/browser-consent-lifecycle";
import {
  trackAdsCtaClick,
  type AdsClickTrackingOptions,
} from "@/components/ads/ads-tracking";

import {
  trackSetupServiceEvent,
  deriveDeviceType,
  type SetupServiceSourcePage,
} from "@/lib/setup-service-analytics";

export type SetupServiceAuthState =
  | "logged_out"
  | "free"
  | "pro"
  | "pro_with_setup_purchased"
  | "pro_with_setup_intake_submitted";

export interface SetupServiceCtaButtonProps {
  /** Where the CTA is mounted — fed straight to source_page in analytics. */
  location: SetupServiceSourcePage;
  /** Server-passed auth+plan state. NEVER resolve client-side (Pitfall 5). */
  authState: SetupServiceAuthState;
  /** Optional plan context — included in payload. */
  planSelected?: "free" | "monthly" | "annual" | null;
  /** Optional offer marker for Stripe/webhook attribution. */
  offer?: string | null;
  /** Optional paid-page tracking to preserve ads CTA analytics. */
  adsTracking?: AdsClickTrackingOptions;
  /** Visible button label per surface (locks per UI-SPEC CTA table). */
  ctaLabel: string;
  /** Optional className override (rare — surface-specific styling). */
  className?: string;
}

/**
 * Smart-route CTA. Renders nothing when the homegrown setup_service flag is
 * off. For logged-out and free buyers, submits a tiny inline <form> bound to
 * the createSetupServiceCheckoutSession server action — Next.js then redirects
 * the browser to Stripe Checkout. For existing-Pro buyers, navigates to the
 * settings billing tab (Phase 9 D-06, preserved).
 */
export function SetupServiceCtaButton(props: SetupServiceCtaButtonProps) {
  const {
    location,
    authState,
    planSelected = null,
    offer = null,
    adsTracking,
    ctaLabel,
    className,
  } = props;

  const router = useRouter();
  const posthog = usePostHog();
  const flagEnabled = useAppFeatureFlag("setup_service");
  const [pending, setPending] = useState(false);
  const gaCookieInputRef = useRef<HTMLInputElement>(null);
  const attributionInputRef = useRef<HTMLInputElement>(null);

  const refreshAttributionInputs = useCallback(() => {
    // These hidden fields are submitted to a durable server/Stripe flow.
    // Never construct or retain optional analytics/ad identifiers unless the
    // authoritative cookie currently grants consent. Read again just before
    // submission because GA can mint `_ga` after the acceptance event.
    const analyticsAccepted = readAnalyticsConsent() === "accepted";
    const advertisingAccepted = readAdvertisingConsent() === "accepted";
    const attribution = advertisingAccepted
      ? readBrowserAttributionWithCurrentUrl()
      : null;
    if (gaCookieInputRef.current) {
      gaCookieInputRef.current.value = analyticsAccepted
        ? (readCookieValue("_ga") ?? "")
        : "";
    }
    if (attributionInputRef.current) {
      attributionInputRef.current.value = attribution
        ? JSON.stringify(attribution)
        : "";
    }
  }, []);

  useEffect(() => {
    refreshAttributionInputs();
    return subscribeToBrowserConsent(refreshAttributionInputs);
    // The feature flag can resolve after the first effect, mounting the refs.
    // Re-run then so a consented visitor does not lose legitimate attribution.
  }, [flagEnabled, refreshAttributionInputs]);

  // Pitfall 2 — treat anything other than `true` as off so we never paint
  // the surface during the flag-resolution race.
  if (flagEnabled !== true) return null;

  const fire = () => {
    refreshAttributionInputs();
    trackSetupServiceEvent("setup_service_cta_clicked", {
      source_page: location,
      device_type: deriveDeviceType(),
      plan_selected: planSelected,
      cta_label: ctaLabel,
    });
    if (adsTracking) {
      trackAdsCtaClick(adsTracking, posthog);
    }
  };

  const buttonClass =
    className ??
    "flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg bg-accent px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-accent-dark disabled:opacity-60 focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2";

  const disabled =
    authState === "pro_with_setup_purchased" ||
    authState === "pro_with_setup_intake_submitted" ||
    pending;

  // Phase 9 D-06 — existing-Pro buyer goes to /settings billing tab.
  if (
    authState === "pro" ||
    authState === "pro_with_setup_purchased" ||
    authState === "pro_with_setup_intake_submitted"
  ) {
    const handleProClick = () => {
      fire();
      if (
        authState === "pro_with_setup_purchased" ||
        authState === "pro_with_setup_intake_submitted"
      ) {
        // Defensive no-op — parent renders the disabled chip.
        return;
      }
      setPending(true);
      router.push("/settings?tab=billing#setup-service");
    };

    return (
      <button
        type="button"
        data-testid="setup-service-cta"
        onClick={handleProClick}
        disabled={disabled}
        aria-label={ctaLabel}
        className={buttonClass}
      >
        {pending ? "Loading…" : ctaLabel}
      </button>
    );
  }

  // logged_out + free both submit the server action which redirects to Stripe.
  // Stripe Checkout collects the buyer's email itself — we don't pre-collect
  // here (D-A2: minimal pre-payment friction). A future iteration could mount
  // a small <input name="email"> above the submit button if we want optional
  // pre-fill.
  return (
    <form
      action={createSetupServiceCheckoutSession}
      onSubmit={refreshAttributionInputs}
    >
      <input ref={gaCookieInputRef} type="hidden" name="gaCookie" />
      <input ref={attributionInputRef} type="hidden" name="attribution" />
      {offer ? <input type="hidden" name="utmOffer" value={offer} /> : null}
      <button
        type="submit"
        data-testid="setup-service-cta"
        onClick={fire}
        disabled={disabled}
        aria-label={ctaLabel}
        className={buttonClass}
      >
        {pending ? "Loading…" : ctaLabel}
      </button>
    </form>
  );
}
